/**
 * Umowy i dokumenty — widok HR (spec 01 §5).
 *
 * Chipy filtrów mają liczniki z realnych danych, a kreator generuje dokument
 * z akt pracownika. Podpis przez mObywatel to integracja spoza MVP — przycisk
 * jest widoczny, ale wyłączony z notą [D21].
 */
import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Document,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Modal,
  Select,
  Spinner,
  StatusPill,
  cx,
  useToast,
} from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { ApiError, api, type HrDocumentItem } from '../../api/client'
import styles from './Documents.module.css'

const FILTERS = ['all', 'szkic', 'do_akceptacji', 'do_podpisu', 'podpisany', 'zarchiwizowany'] as const
type Filter = (typeof FILTERS)[number]

/** Pozycje świadectwa spoza ewidencji — wpisuje je kadrowa (Fala 5, grupa 49). */
const CERTIFICATE_FIELDS = [
  'childcareLeave',
  'carerLeave',
  'forceMajeure',
  'unpaidLeaveBasis',
  'militaryService',
  'specialConditions',
  'noticeCompensation',
] as const
type CertificateField = (typeof CERTIFICATE_FIELDS)[number]

interface GeneratorError {
  readonly key: string
  readonly params?: Record<string, string>
}

interface ErrorLabels {
  /** Miesiąc „rrrr-mm" słowami („wrzesień 2026"). */
  readonly month: (month: string) => string
  /** Rodzaj umowy słowami. */
  readonly contract: (contractType: string) => string
}

/** Odmowa kreatora z API jako zdanie — zamiast „nie udało się" (Fala 5, grupy 14, 15, 49). */
function generatorError(caught: unknown, labels: ErrorLabels): GeneratorError {
  if (!(caught instanceof ApiError)) return { key: 'hrDocuments.errorGeneric' }
  switch (caught.code) {
    case 'NO_TERMINATION':
      return { key: 'hrDocuments.errorNoTermination' }
    case 'TEMPLATE_CONTRACT_MISMATCH': {
      const contract = typeof caught.details.contractType === 'string' ? caught.details.contractType : null
      return {
        key: 'hrDocuments.errorContractMismatch',
        params: { contract: contract ? labels.contract(contract) : '—' },
      }
    }
    case 'CONTRACT_TYPE_MISSING':
      return { key: 'hrDocuments.errorContractTypeMissing' }
    case 'NO_TERMS_CHANGE':
      return { key: 'hrDocuments.errorNoTermsChange' }
    case 'EMPLOYMENT_NOT_ACTIVE':
      return { key: 'hrDocuments.errorEmploymentNotActive' }
    case 'NEW_CONTRACT_REQUIRED':
      return { key: 'hrDocuments.errorNewContractRequired' }
    case 'UNPAID_LEAVE_NOT_RECORDED':
      return { key: 'hrDocuments.errorUnpaidLeaveNotRecorded' }
    case 'CERTIFICATE_FACTS_INCOMPLETE': {
      const endDate = typeof caught.details.endDate === 'string' ? caught.details.endDate : null
      const month = typeof caught.details.month === 'string' ? caught.details.month : null
      if (caught.details.reason === 'unsettled_month' && month) {
        return { key: 'hrDocuments.errorCertificateUnsettled', params: { month: labels.month(month) } }
      }
      if (caught.details.reason === 'stale_month' && month) {
        return { key: 'hrDocuments.errorCertificateStale', params: { month: labels.month(month) } }
      }
      return {
        key: 'hrDocuments.errorCertificateBeforeEnd',
        params: { date: endDate ? formatDate(endDate, 'dot') : '' },
      }
    }
    default:
      return { key: 'hrDocuments.errorGeneric' }
  }
}

const STATUS_TONE: Record<HrDocumentItem['status'], 'pink' | 'warning' | 'success' | 'neutral'> = {
  szkic: 'pink',
  do_akceptacji: 'warning',
  do_podpisu: 'pink',
  podpisany: 'success',
  zarchiwizowany: 'neutral',
}

export function Documents() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = { employee: useId(), type: useId(), certificate: useId() }

  const [filter, setFilter] = useState<Filter>('all')
  const [generatorOpen, setGeneratorOpen] = useState(false)
  const [form, setForm] = useState({ employeeId: '', type: 'zaswiadczenie' })
  const [certificate, setCertificate] = useState<Partial<Record<CertificateField, string>>>({})
  const [error, setError] = useState<string | null>(null)
  const [generateError, setGenerateError] = useState<GeneratorError | null>(null)
  const [xmlIssues, setXmlIssues] = useState<string[]>([])
  const errorRef = useRef<HTMLDivElement>(null)

  const documents = useQuery({
    queryKey: ['documents', filter],
    queryFn: () => api.hrDocuments(filter),
  })
  const employees = useQuery({
    queryKey: ['employees', ''],
    queryFn: () => api.employees(),
    enabled: generatorOpen,
  })
  /*
   * Szablony dla wybranej osoby liczy serwer — tą samą regułą wersji warunków
   * co przy wystawieniu, nie z kolumny rodzaju umowy (przegląd Fali 5).
   */
  const options = useQuery({
    queryKey: ['document-generator-options', form.employeeId],
    queryFn: () => api.documentGeneratorOptions(form.employeeId),
    enabled: generatorOpen && form.employeeId !== '',
  })
  const allowedForPerson = options.data?.types.filter((row) => row.allowed).map((row) => row.type)
  /* Szablon niedozwolony dla wybranej osoby przechodzi na pierwszy dozwolony. */
  const selectedType =
    allowedForPerson !== undefined && allowedForPerson.length > 0 && !allowedForPerson.includes(form.type)
      ? allowedForPerson[0]!
      : form.type

  /* Odmowa ma być widoczna i ogłoszona — okno przewija się razem ze stopką. */
  useEffect(() => {
    if (generateError === null) return
    errorRef.current?.scrollIntoView?.({ block: 'nearest' })
    errorRef.current?.focus()
  }, [generateError])

  const monthLabel = (month: string): string => {
    const [year, monthNumber] = month.split('-')
    const name = t(`hrPayroll.months.${Number(monthNumber)}`)
    /* W zdaniu po polsku nazwa miesiąca małą literą („za wrzesień 2026"). */
    return `${i18n.language.startsWith('pl') ? name.toLocaleLowerCase('pl-PL') : name} ${year}`
  }
  const errorLabels: ErrorLabels = {
    month: monthLabel,
    contract: (contractType) => t(`contractType.${contractType}`, { defaultValue: contractType }),
  }

  const openGenerator = () => {
    setError(null)
    setForm({ employeeId: '', type: 'zaswiadczenie' })
    setCertificate({})
    setGenerateError(null)
    setGeneratorOpen(true)
  }
  const closeGenerator = () => {
    setGeneratorOpen(false)
    setGenerateError(null)
  }

  const generate = useMutation({
    mutationFn: (type: string) =>
      api.generateDocument({
        employeeId: form.employeeId,
        type,
        ...(type === 'swiadectwo' ? { certificate } : {}),
      }),
    onMutate: () => setGenerateError(null),
    onSuccess: (result) => {
      toast.show(t(result.pdf ? 'hrDocuments.generated' : 'hrDocuments.generatedNoPdf'), {
        tone: result.pdf ? 'success' : 'info',
      })
      setGeneratorOpen(false)
      setGenerateError(null)
      /* Pozycje świadectwa należą do tej osoby — nie przechodzą na następną. */
      setCertificate({})
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    /* Odmowa zostaje w oknie kreatora — tam, gdzie kadrowa może ją naprawić. */
    onError: (caught) => setGenerateError(generatorError(caught, errorLabels)),
  })

  /*
   * Plik XML deklaracji PIT-11 (Etap 27). Osobna mutacja, nie nawigacja jak przy
   * PDF-ie: gdy w aktach brakuje kodu urzędu albo daty urodzenia, serwer podaje
   * listę braków i chcemy ją pokazać zdaniami, nie surowym JSON-em.
   */
  const downloadXml = useMutation({
    mutationFn: (id: string) => api.downloadPit11Xml(id),
    onSuccess: () => {
      toast.show(t('hrDocuments.xmlDownloaded'), { tone: 'success' })
      setXmlIssues([])
    },
    onError: (caught) => {
      const issues = caught instanceof ApiError ? caught.issues : []
      setXmlIssues(issues.length > 0 ? [...issues] : ['generic'])
    },
  })

  /* PDF przez fetch: brak serwisu albo treści to komunikat, nie surowy JSON (grupa 48). */
  const downloadPdf = useMutation({
    mutationFn: (item: HrDocumentItem) =>
      api.downloadHrDocumentPdf(item.id, `${item.title}.pdf`),
    onError: (caught) => {
      const code = caught instanceof ApiError ? caught.code : null
      toast.show(
        t(
          code === 'PDF_UNAVAILABLE'
            ? 'hrDocuments.pdfUnavailable'
            : code === 'FILE_MISSING'
              ? 'hrDocuments.noPdf'
              : 'hrDocuments.downloadFailed',
        ),
        { tone: 'danger' },
      )
    },
  })

  const transition = useMutation({
    mutationFn: (input: {
      id: string
      action: 'submit-for-approval' | 'approve' | 'sign' | 'archive'
    }) => api.documentTransition(input.id, input.action),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: () => setError('hrDocuments.errorGeneric'),
  })

  if (documents.isPending) return <Spinner label={t('common.loading')} />
  if (documents.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const { items, counts, generatableTypes } = documents.data
  /*
   * Szablony opisują stosunek pracy (grupa 14): po wyborze osoby kreator
   * pokazuje tylko te, które serwer pozwala jej wystawić.
   */
  const allowedTypes =
    form.employeeId === '' ? generatableTypes : (allowedForPerson ?? [])
  const optionsPending = form.employeeId !== '' && options.isPending
  const noTemplates = form.employeeId !== '' && allowedForPerson !== undefined && allowedForPerson.length === 0
  /* Brak rodzaju umowy w aktach to nie umowa cywilna — inny komunikat (weryfikacja na żywo Fali 5). */
  const contractTypeMissing =
    noTemplates && (options.data?.types ?? []).every((row) => row.contractType === null)

  return (
    <>
      <div className={styles.header}>
        <div>
          <p className={styles.lead}>{t('hrDocuments.lead')}</p>
          <p className={styles.note}>{t('hrDocuments.signNote')}</p>
        </div>
        <Button onClick={openGenerator}>{t('hrDocuments.generate')}</Button>
      </div>

      {xmlIssues.length > 0 ? (
        <InfoBanner tone="warning" className={styles.banner}>
          <strong>{t('hrDocuments.xmlErrorTitle')}</strong>{' '}
          {xmlIssues
            .map((issue) =>
              issue === 'generic'
                ? t('hrDocuments.xmlErrorGeneric')
                : t(`hrDocuments.xmlIssues.${issue}`, {
                    defaultValue: t('hrDocuments.xmlErrorGeneric'),
                  }),
            )
            .join(' ')}
        </InfoBanner>
      ) : null}

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      <div className={styles.chips}>
        {FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            className={cx(styles.chip, filter === key && styles.chipActive)}
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
          >
            {t(`hrDocuments.filters.${key}`)}
            <span className={styles.chipCount}>{counts[key] ?? 0}</span>
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<Document size={28} />}
          title={t('hrDocuments.empty')}
          description={t('hrDocuments.emptyBody')}
        />
      ) : (
        <div className={styles.grid}>
          {items.map((item) => (
            <Card key={item.id} pad="md" className={styles.card}>
              <div className={styles.cardHead}>
                <span className={styles.cardIcon} aria-hidden="true">
                  <Document size={18} />
                </span>
                <StatusPill tone={STATUS_TONE[item.status]} dot>
                  {t(`hrDocuments.status.${item.status}`)}
                </StatusPill>
              </div>
              <p className={styles.cardTitle}>{item.title}</p>
              <p className={styles.cardMeta}>
                {t(`hrDocuments.types.${item.type}`, { defaultValue: item.type })}
                {item.firstName ? ` · ${item.firstName} ${item.lastName}` : ''}
              </p>
              {item.date ? (
                <p className={styles.cardDate}>{formatDate(item.date, 'dot')}</p>
              ) : null}

              <div className={styles.cardActions}>
                {item.status === 'szkic' ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={transition.isPending}
                    onClick={() =>
                      transition.mutate({ id: item.id, action: 'submit-for-approval' })
                    }
                  >
                    {t('hrDocuments.submitForApproval')}
                  </Button>
                ) : null}
                {item.status === 'do_akceptacji' ? (
                  <Button
                    size="sm"
                    disabled={transition.isPending}
                    onClick={() => transition.mutate({ id: item.id, action: 'approve' })}
                  >
                    {t('hrDocuments.approve')}
                  </Button>
                ) : null}
                {item.status === 'do_podpisu' ? (
                  <>
                    {/*
                      Podpis powstaje POZA systemem (mObywatel, podpis
                      kwalifikowany — integracji nie ma), więc przycisk oznacza
                      FAKT podpisania, a nie udaje wysyłki do podpisu (Etap 15).
                    */}
                    <Button
                      size="sm"
                      disabled={transition.isPending}
                      onClick={() => transition.mutate({ id: item.id, action: 'sign' })}
                    >
                      {t('hrDocuments.markSigned')}
                    </Button>
                    <Button size="sm" variant="secondary" disabled title={t('hrDocuments.signNote')}>
                      {t('hrDocuments.signSoon')}
                    </Button>
                  </>
                ) : null}
                {/*
                  PIT-11 od 2019 r. składa się elektronicznie, więc obok wydruku
                  dla pracownika wydajemy plik XML do bramki e-Deklaracje. Sami
                  go nie wysyłamy i nie podpisujemy (Etap 27, ADR-0028).
                */}
                {item.type === 'pit11' ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={downloadXml.isPending}
                    title={t('hrDocuments.xmlHint')}
                    onClick={() => {
                      setXmlIssues([])
                      downloadXml.mutate(item.id)
                    }}
                  >
                    {t('hrDocuments.downloadXml')}
                  </Button>
                ) : null}
                {/* Plik albo zapisana treść wystarczą — PDF powstaje przy pobraniu (grupa 48). */}
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={(item.fileId === null && !item.hasContent) || downloadPdf.isPending}
                  {...(item.fileId !== null || item.hasContent
                    ? { onClick: () => downloadPdf.mutate(item) }
                    : { title: t('hrDocuments.noPdf') })}
                >
                  {t('hrDocuments.download')}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={generatorOpen}
        onClose={closeGenerator}
        title={t('hrDocuments.generatorTitle')}
        footer={
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={closeGenerator}>
              {t('hrDocuments.cancel')}
            </Button>
            <Button
              disabled={form.employeeId === '' || optionsPending || noTemplates || generate.isPending}
              onClick={() => generate.mutate(selectedType)}
            >
              {generate.isPending ? (
                <Spinner label={t('hrDocuments.generatorSubmitting')} />
              ) : (
                t('hrDocuments.generatorSubmit')
              )}
            </Button>
          </div>
        }
      >
        <p className={styles.modalLead}>{t('hrDocuments.generatorSubtitle')}</p>
        {generateError !== null ? (
          <div ref={errorRef} role="alert" tabIndex={-1} className={styles.generatorError}>
            <InfoBanner tone="danger" className={styles.banner}>
              {t(generateError.key, generateError.params ?? {})}
            </InfoBanner>
          </div>
        ) : null}
        <FormField label={t('hrDocuments.generatorEmployee')} htmlFor={ids.employee}>
          <Select
            value={form.employeeId}
            onChange={(event) => {
              setGenerateError(null)
              /* Pozycje świadectwa jednej osoby nie przechodzą na świadectwo innej. */
              setCertificate({})
              setForm({ ...form, employeeId: event.target.value })
            }}
          >
            <option value="">—</option>
            {(employees.data?.items ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.firstName} {employee.lastName} · {employee.displayId}
              </option>
            ))}
          </Select>
        </FormField>
        {optionsPending ? (
          <Spinner label={t('common.loading')} />
        ) : noTemplates ? (
          <InfoBanner tone="info" className={styles.banner}>
            {t(contractTypeMissing ? 'hrDocuments.generatorContractTypeMissing' : 'hrDocuments.generatorNoTemplates')}
          </InfoBanner>
        ) : (
          <FormField label={t('hrDocuments.generatorType')} htmlFor={ids.type}>
            <Select
              value={selectedType}
              onChange={(event) => {
                setGenerateError(null)
                setForm({ ...form, type: event.target.value })
              }}
            >
              {allowedTypes.map((type) => (
                <option key={type} value={type}>
                  {t(`hrDocuments.types.${type}`, { defaultValue: type })}
                </option>
              ))}
            </Select>
          </FormField>
        )}
        {selectedType === 'swiadectwo' && !noTemplates && !optionsPending ? (
          <fieldset className={styles.certificate}>
            <legend className={styles.certificateTitle}>{t('hrDocuments.certificate.title')}</legend>
            <p id={`${ids.certificate}-lead`} className={styles.modalLead}>
              {t('hrDocuments.certificate.lead')}
            </p>
            {CERTIFICATE_FIELDS.map((field) => (
              <FormField
                key={field}
                label={t(`hrDocuments.certificate.${field}`)}
                htmlFor={`${ids.certificate}-${field}`}
                {...(field === 'unpaidLeaveBasis'
                  ? { hint: t('hrDocuments.certificate.unpaidLeaveBasisHint') }
                  : {})}
              >
                <Input
                  id={`${ids.certificate}-${field}`}
                  /* Reguła „puste pole = brak do uzupełnienia" czytana przy każdym polu. */
                  aria-describedby={`${ids.certificate}-lead`}
                  value={certificate[field] ?? ''}
                  maxLength={500}
                  onChange={(event) => setCertificate({ ...certificate, [field]: event.target.value })}
                />
              </FormField>
            ))}
          </fieldset>
        ) : null}
      </Modal>
    </>
  )
}
